import { usePricingAggregationsFilter, usePricingTierFilters, useRoleAuth } from '@src/hooks';
import {
  OrgConfigurationKey,
  useCustomerPricingTierContext,
  useCustomerSecuritiesBySymbol,
  useOrgConfiguration,
  usePricingRules,
} from '@src/providers';
import {
  ACTION,
  AggregationType,
  ButtonVariants,
  DEFAULT_BULK_EDIT_MAX_ROWS,
  IconName,
  useAggregationsContext,
  useCurrenciesContext,
  useDisclosure,
  useFeeCurrenciesFilter,
  useMarketFeeModesFilter,
  useSecuritiesContext,
  useSymbolsFilter,
  type AllInTier,
  type FeeTier,
  type Security,
} from '@talos/kyoko';
import { filter, isNil } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useEditPricingRule } from '../CustomerPricing/useEditPricingRule';
import { DEALER_FEE_MODE_OPTIONS } from '../utils';
import {
  getCustomerPricingTiersAllInTierDrawerOptions,
  getCustomerPricingTiersAllInTierOverridesDrawerOptions,
  getCustomerPricingTiersFeeTierDrawerOptions,
  getCustomerPricingTiersFeeTierOverridesDrawerOptions,
} from './utils';

export const CustomerPricingTiersLoad = {
  AllIn: 'all-in',
  Fee: 'fee',
  Both: 'both',
} as const;
export type CustomerPricingTiersLoad = (typeof CustomerPricingTiersLoad)[keyof typeof CustomerPricingTiersLoad];

/** Shared state for All-in Tiers and Fee Tiers EntityAdmin surfaces (page tabs + Spaces panels). */
export function useCustomerPricingTiersShared(load: CustomerPricingTiersLoad = CustomerPricingTiersLoad.Both) {
  const { isAuthorized } = useRoleAuth();
  const { securitiesBySymbol } = useSecuritiesContext();
  const { getConfig } = useOrgConfiguration();

  const [allInTiers, setAllInTiers] = useState<AllInTier[]>();
  const [feeTiers, setFeeTiers] = useState<FeeTier[]>();
  const { getAllInTiers, getFeeTiers } = useCustomerPricingTierContext();
  useEffect(() => {
    if (load === CustomerPricingTiersLoad.AllIn || load === CustomerPricingTiersLoad.Both) {
      getAllInTiers(true).then(({ data }) => {
        setAllInTiers(data);
      });
    }
    if (load === CustomerPricingTiersLoad.Fee || load === CustomerPricingTiersLoad.Both) {
      getFeeTiers(true).then(({ data }) => {
        setFeeTiers(data);
      });
    }
  }, [getAllInTiers, getFeeTiers, load]);

  const { aggregationsByName } = useAggregationsContext();
  const { currenciesList } = useCurrenciesContext();
  const customerSecuritiesBySymbol = useCustomerSecuritiesBySymbol();

  const filteredCurrencies = useMemo(() => currenciesList.map(currency => currency.Symbol), [currenciesList]);
  const filteredSecurities = useMemo((): Security[] => {
    if (!customerSecuritiesBySymbol) {
      return [];
    }

    return filter(Array.from(securitiesBySymbol.values()), security => customerSecuritiesBySymbol.has(security.Symbol));
  }, [customerSecuritiesBySymbol, securitiesBySymbol]);

  const { allInTierFilter, feeTierFilter } = usePricingTierFilters({
    allInTierField: 'Tier',
    feeTierField: 'Tier',
  });
  const marketFeeModeFilter = useMarketFeeModesFilter();
  const feeCurrencyFilter = useFeeCurrenciesFilter(filteredCurrencies);
  const pricingAggregationFilter = usePricingAggregationsFilter();
  const symbolFilter = useSymbolsFilter(filteredSecurities);

  const customerAggregations = useMemo(() => {
    if (aggregationsByName != null) {
      return Array.from(aggregationsByName.values()).filter(
        aggregation => aggregation.AggregationType === AggregationType.Customer
      );
    }
  }, [aggregationsByName]);

  const allInTiersDrawerOptions = useMemo(() => {
    if (customerAggregations != null) {
      return getCustomerPricingTiersAllInTierDrawerOptions(
        customerAggregations,
        DEALER_FEE_MODE_OPTIONS,
        currenciesList
      );
    }
  }, [currenciesList, customerAggregations]);

  const allInTiersOverridesDrawerOptions = useMemo(() => {
    if (customerAggregations != null && allInTiers != null) {
      const allInTiersGroups = allInTiers.filter(tier => tier.Symbol == null);
      return getCustomerPricingTiersAllInTierOverridesDrawerOptions(
        customerAggregations,
        DEALER_FEE_MODE_OPTIONS,
        currenciesList,
        allInTiersGroups,
        filteredSecurities
      );
    }
  }, [allInTiers, currenciesList, customerAggregations, filteredSecurities]);

  const feeTiersDrawerOptions = useMemo(() => {
    if (currenciesList != null) {
      return getCustomerPricingTiersFeeTierDrawerOptions(DEALER_FEE_MODE_OPTIONS, currenciesList);
    }
  }, [currenciesList]);

  const feeTiersOverridesDrawerOptions = useMemo(() => {
    if (currenciesList != null && feeTiers != null) {
      const feeTiersGroups = feeTiers.filter(tier => tier.Symbol == null);
      return getCustomerPricingTiersFeeTierOverridesDrawerOptions(
        DEALER_FEE_MODE_OPTIONS,
        currenciesList,
        feeTiersGroups,
        filteredSecurities
      );
    }
  }, [currenciesList, filteredSecurities, feeTiers]);

  const getAllInTiersDrawerOptions = useCallback(
    (entity?: AllInTier, addingChildEntity?: boolean) => {
      if (addingChildEntity) {
        return allInTiersOverridesDrawerOptions;
      }
      const isGroupRow = entity?.Symbol == null;
      return isGroupRow ? allInTiersDrawerOptions : allInTiersOverridesDrawerOptions;
    },
    [allInTiersDrawerOptions, allInTiersOverridesDrawerOptions]
  );

  const getFeeTiersDrawerOptions = useCallback(
    (entity?: FeeTier, addingChildEntity?: boolean) => {
      if (addingChildEntity) {
        return feeTiersOverridesDrawerOptions;
      }
      const isGroupRow = entity?.Symbol == null;
      return isGroupRow ? feeTiersDrawerOptions : feeTiersOverridesDrawerOptions;
    },
    [feeTiersDrawerOptions, feeTiersOverridesDrawerOptions]
  );

  const allInTiersFilterableProperties = useMemo(() => {
    const properties = [
      allInTierFilter,
      symbolFilter,
      marketFeeModeFilter,
      feeCurrencyFilter,
      pricingAggregationFilter,
    ];
    return properties.every(prop => !isNil(prop)) ? properties : undefined;
  }, [symbolFilter, allInTierFilter, marketFeeModeFilter, feeCurrencyFilter, pricingAggregationFilter]);

  const feeTiersFilterableProperties = useMemo(() => {
    const properties = [feeTierFilter, symbolFilter, marketFeeModeFilter, feeCurrencyFilter, pricingAggregationFilter];
    return properties.every(prop => !isNil(prop)) ? properties : undefined;
  }, [symbolFilter, feeTierFilter, marketFeeModeFilter, feeCurrencyFilter, pricingAggregationFilter]);

  const defaultPricingRuleDialog = useDisclosure();

  const defaultPricingRuleAction = useMemo(
    () => ({
      label: 'Default Pricing Rule',
      onClick: () => defaultPricingRuleDialog.open(),
      variant: ButtonVariants.Priority,
      startIcon: IconName.Pencil,
    }),
    [defaultPricingRuleDialog]
  );

  const handleEditPricingRule = useEditPricingRule({ onSettled: undefined });
  const { globalDefault } = usePricingRules();
  const canEditDealer = isAuthorized(ACTION.EDIT_DEALER);
  const maxBulkEditRows = getConfig(OrgConfigurationKey.EntityAdminMaxBulkEditRows, DEFAULT_BULK_EDIT_MAX_ROWS);

  const getChildEntityDisplayName = useCallback(
    (entity: AllInTier | FeeTier) => {
      if (entity.Symbol == null) {
        return '';
      }
      return customerSecuritiesBySymbol?.get(entity.Symbol)?.DisplaySymbol ?? entity.Symbol;
    },
    [customerSecuritiesBySymbol]
  );

  return {
    canEditDealer,
    maxBulkEditRows,
    globalDefault,
    getAllInTiersDrawerOptions,
    getFeeTiersDrawerOptions,
    allInTiersFilterableProperties,
    feeTiersFilterableProperties,
    defaultPricingRuleDialog,
    defaultPricingRuleAction,
    handleEditPricingRule,
    getChildEntityDisplayName,
  };
}
